/**
 * Sunucu API'sinin tipli istemcisi.
 *
 * İstemci **kararı kendisi vermez**; yalnızca tercihlerini bildirir ve
 * `/api/playback/decide` yanıtını uygular (spec §karar hattı sunucudadır).
 *
 * Web/PWA'da tüm istekler `same-origin`: oturum `HttpOnly` çerez taşır.
 * Paketlenmiş Capacitor build'inde (`VITE_API_BASE_URL` dolu) API'ye
 * `Authorization: Bearer` ile gidilir; token native Keystore'da saklanır.
 */

import { API_BASE_URL, absolutizeApiUrls, apiUrl, usesBearerAuth } from './apiBase.ts';
import { clearAuthToken, getAuthToken, setAuthToken } from './authToken.ts';
import { mediaUrl } from './mediaToken.ts';
import { recordSwallowed } from './swallowed.ts';

export interface User {
  id: string;
  username: string;
  displayName: string;
  role: string;
  has2fa?: boolean;
}

/**
 * Bir öğedeki altyazı.
 *
 * `key` altyazının **kimliğidir** ve URL'e konur: `e3` = dosyanın içindeki
 * 3. akış, `s0` = yan dosya (ayrı `.srt`) (ADR-016). İki kaynak tek bir
 * sayı alanında yaşayamazdı: gömülü akış `3` ile yan dosya `3` karışırdı.
 */
export interface Subtitle {
  /** Altyazı anahtarı (sunucu üretir, istemci olduğu gibi gönderir). */
  key: string;
  codec: string;
  language: string | null;
  title: string | null;
  forced: boolean;
  /** Tarayıcıda yumuşak oynatılabilir mi? */
  softPlayable: boolean;
  /** Yan dosya mı (filmin içine gömülü değil)? */
  external: boolean;
}

/** Öğe altyazı hazırlık durumu (Faz 3). */
export type SubtitleState = 'ready' | 'preparing' | 'idle' | 'burn_required';

export interface SubtitleStatus {
  streams: Array<{ key: string; state: SubtitleState }>;
}

export interface AudioTrack {
  /** `0:a:N` içindeki N. */
  index: number;
  codec: string;
  channels: number;
  language: string | null;
  title: string | null;
}

export interface Item {
  id: string;
  libraryId: string;
  kind: 'movie' | 'show' | 'season' | 'episode';
  title: string;
  year: number | null;
  seasonNumber: number | null;
  episodeNumber: number | null;
  durationMs: number | null;
  /** ffprobe teknik tanımı; yoksa `null` (uydurma rozet gösterilmez). */
  technical?: {
    width: number | null;
    height: number | null;
    videoCodec: string | null;
    audioCodec: string | null;
    container: string | null;
    /** Eski sunucularda alan yok; ses seçici bu durumda gizlenir. */
    audioTracks?: AudioTrack[];
  } | null;
  progress: { positionMs: number; state: string; updatedAt?: number } | null;
  isWatched: boolean;
  /** "Listem" durumu (Tasarım V4, Öneri 9). */
  inWatchlist?: boolean;
  /** Yerel müzik etiketleri; müzik dışı içerikte `null`. */
  music?: {
    artist: string | null;
    albumArtist: string | null;
    album: string | null;
    trackNumber: number | null;
    discNumber: number | null;
  } | null;
  /**
   * Parçanın albüm kimliği (klasör yolundan türetilir). Müzik dışı içerikte
   * veya eski sunucuda alan gelmezse `undefined`/`null`.
   */
  albumId?: string | null;
  /** Albüm kapağı; oynatıcı ve kilit ekranı parça kapağından önce bunu kullanır. */
  albumCoverUrl?: string | null;
  /** Arama sonucunda sıralamayı açıklayan neden. */
  matchReason?: SearchReason;
  /**
   * Metadata afişi/backdrop'u — kendi sunucumuzun proxy URL'si.
   * Metadata yoksa/eski sunucuda alan gelmezse yerel thumbnail kullanılır.
   */
  posterUrl?: string | null;
  backdropUrl?: string | null;
  /** Afiş/backdrop kaynağı: yükleme, klasör yan dosyası veya sağlayıcı. */
  artwork?: {
    posterSource: 'upload' | 'sidecar' | null;
    backdropSource: 'upload' | 'sidecar' | null;
  } | null;
  /** Sunucunun imzaladığı yerel thumbnail URL'si (ADR-012). */
  thumbnailUrl?: string | null;
  /** Yalnızca `/api/me/continue` yanıtında gelir. */
  resumePositionMs?: number;
  /**
   * Altyazılar yalnız `/api/items/:id` (detay) yanıtında gelir; liste
   * uçlarında yoktur — kart üzerinde gereksinim duyulmaz.
   */
  subtitles?: Subtitle[];
}

export interface ItemDetail extends Item {
  children: Item[];
  episodes?: Item[];
}

export interface ItemCollection {
  collection: { id: string; name: string } | null;
  items: Item[];
}

/** Harici metadata (Tasarım V4, Öneri 13). */
export interface ItemMetadata {
  provider: string;
  externalId: string;
  status: 'matched' | 'pending';
  confidence: number;
  title: string | null;
  /** Manuel düzenleme yoksa öğenin kendi yılı. */
  year: number | null;
  overview: string | null;
  tagline: string | null;
  releaseDate: string | null;
  runtimeMinutes: number | null;
  rating: number | null;
  votes: number | null;
  /** IMDb puanı (OMDb); TMDB puanı `rating` alanında kalır. */
  imdbRating: number | null;
  imdbVotes: number | null;
  genres: string[];
  cast: string[];
  collection: { id: string; name: string } | null;
  posterUrl: string | null;
  backdropUrl: string | null;
  fetchedAt: number;
  /** Kullanıcı en az bir alanı elle düzenledi mi? */
  hasOverride: boolean;
}

/** Kullanıcının elle düzenleyebildiği metadata alanları. */
export interface MetadataOverrideInput {
  title?: string | null;
  year?: number | null;
  overview?: string | null;
  tagline?: string | null;
  releaseDate?: string | null;
  runtimeMinutes?: number | null;
  genres?: string[];
  cast?: string[];
}

/** Yönetici metadata gelen kutusu kaydı (V5-12). */
export interface AdminMetadataItem {
  itemId: string;
  title: string;
  year: number | null;
  kind: string;
  libraryId: string;
  libraryName: string;
  status: 'matched' | 'pending' | null;
  confidence: number | null;
  externalId: string | null;
  metadataTitle: string | null;
  posterPath: string | null;
  updatedAt: number | null;
  reason: 'missing' | 'pending' | 'low_confidence' | 'missing_image';
}

export interface AdminUser {
  id: string;
  username: string;
  displayName: string;
  role: 'user' | 'admin';
  createdAt: number;
  disabledAt: number | null;
  has2fa: boolean;
  activeSessions: number;
}

export interface AdminSystem {
  state: 'ok' | 'warning' | 'critical';
  disk: {
    state: DiskState;
    filesystem: FilesystemUsage | null;
    transcode: Omit<CacheUsage, 'path'>;
    remoteCache: Omit<CacheUsage, 'path'> | null;
  };
  backup: {
    state: 'ok' | 'stale' | 'missing' | 'unconfigured';
    latest: string | null;
    ageHours: number | null;
    maxAgeHours: number;
    stale: boolean;
  };
  queues: {
    transcode: {
      queued: number;
      running: number;
      ready: number;
      failed: number;
      cancelled: number;
      total: number;
      concurrency: number;
      oldestRunningMs: number | null;
    };
    metadata: { pending: number; running: boolean } | null;
  };
  scans: { latestAt: number | null; libraries: Array<{ name: string; scannedAt: number | null }> };
  sessions: { active: number };
  catalog: { libraries: number };
  warnings: string[];
}

export interface AdminSession {
  id: string;
  userId: string;
  username: string;
  displayName: string;
  role: string;
  deviceName: string;
  createdAt: number;
  lastUsedAt: number | null;
  expiresAt: number;
  current: boolean;
}

/** Sağlayıcı arama sonucu. */
export interface MetadataCandidate {
  externalId: string;
  title: string;
  year: number | null;
  overview: string | null;
  posterUrl: string | null;
  kind: 'movie' | 'show';
}

/** Canlı TV (IPTV) çalma listesi kaynağı. */
export interface IptvSource {
  id: string;
  name: string;
  kind: string;
  channelCount: number;
  lastSyncAt: number | null;
  lastError: string | null;
  /** Yönetici tarafından paylaşıldı mı? */
  shared: boolean;
}

/** Ölçülen tek kanalın sonucu. */
export interface IptvProbeChannel {
  name: string;
  group: string | null;
  /**
   * Sonuç: `ok` (açılıyor), `blocked` (coğrafi kısıt), `not_found` (ölü kanal),
   * `timeout`, `network`, `blocked_address`, `error`.
   */
  reason: string;
  status: number | null;
}

/** Bir çalma listesinin ölçüm sonucu. */
export interface IptvProbeResult {
  url: string;
  total: number;
  tested: number;
  working: number;
  reasons: Record<string, number>;
  channels: IptvProbeChannel[];
  error: string | null;
}

/** Ölçülmüş önerilen liste. */
export interface IptvRecommendedSource {
  id: string;
  name: string;
  url: string;
  note: string;
  probe: IptvProbeResult;
}

/**
 * Canlı TV kanalı.
 *
 * **Akış adresi içermez**: sağlayıcının adresi sunucuda kalır, istemci
 * yalnız kimlik ve görünen bilgileri görür.
 */
export interface IptvChannel {
  id: string;
  sourceId: string;
  /** Rehber (EPG) eşleştirme anahtarı. */
  key: string;
  name: string;
  group: string | null;
  logo: string | null;
  container: string;
  channelNumber: number | null;
  hasArchive: boolean;
  archiveDays: number | null;
  tvgId: string | null;
}

/** Rehberdeki tek bir program. Zamanlar UTC epoch ms. */
export interface IptvProgramme {
  startsAt: number;
  stopsAt: number;
  title: string;
  description: string | null;
  category: string | null;
}

/** Rehberdeki kanal ve programları. */
export interface IptvGuideChannel {
  key: string;
  name: string;
  icon: string | null;
  programmes: IptvProgramme[];
}

export interface Library {
  id: string;
  name: string;
  kind: 'movie' | 'show' | 'mixed' | 'music';
  scannedAt: number | null;
  /** `cold` = evdeki bilgisayar (WireGuard); kalite sunucu tarafından düşürülür. */
  layer: 'hot' | 'cold';
  /**
   * Soğuk katmanın canlı ölçümü. `true` = erişilebilir.
   * Sıcak katmanda her zaman `true`.
   */
  reachable: boolean;
  /** Erişilemiyorsa neden (kullanıcı dilinde). */
  unavailableReason: string | null;
  itemCount: number;
  /**
   * Ana sayfadaki kütüphane kartında önizleme kapağı için öğe kimliği.
   * Kütüphane boşsa `null` (eski sunucularda alan hiç gelmeyebilir).
   */
  previewItemId?: string | null;
  /** Önizleme öğesinin metadata afişi (sunucu proxy'si). */
  previewPosterUrl?: string | null;
  /** Önizleme öğesinin imzalı yerel thumbnail URL'si (ADR-012). */
  previewThumbnailUrl?: string | null;
}

/**
 * Kütüphane özeti — istatistikler + son eklenenler.
 *
 * `durationMs` toplam içerik süresidir; "kaç saat eder" sorusunu tek sayıyla
 * yanıtlar. `newestAt` son eklenen içeriğin eklenme anıdır.
 */
export type SearchKind = 'all' | 'movie' | 'show' | 'episode' | 'season' | 'music';
export type SearchReason = 'exact_title' | 'title_prefix' | 'title_contains' | 'token_match';

export interface SearchResponse {
  query: string;
  hits: Item[];
  total: number;
  filters?: { kind: SearchKind; yearFrom: number | null; yearTo: number | null };
}

export interface MusicAlbum {
  id: string;
  title: string;
  artist: string;
  year: number | null;
  /** Eski sunucularda alan yok; bu durumda boş dizi kabul edilir. */
  genres?: string[];
  notes?: string | null;
  trackCount: number;
  durationMs: number;
  coverItemId: string | null;
  /** Kapak kaynağı: yükleme, klasör yan dosyası veya gömülü kapak. */
  coverSource?: 'upload' | 'sidecar' | 'embedded' | null;
  /** Sunucunun imzaladığı kapak thumbnail URL'si (ADR-012). */
  coverThumbnailUrl?: string | null;
}

/** Albüm düzeyinde kullanıcı düzenlemesi. */
export interface MusicAlbumPatch {
  title?: string | null;
  artist?: string | null;
  year?: number | null;
  genres?: string[];
  notes?: string | null;
}

export interface MusicArtist {
  id: string;
  name: string;
  albumCount: number;
  trackCount: number;
  coverItemId: string | null;
  coverThumbnailUrl?: string | null;
}

export interface MusicArchive {
  albums: MusicAlbum[];
  artists: MusicArtist[];
}

export interface MusicAlbumDetail {
  album: MusicAlbum;
  tracks: Item[];
}

export interface MusicArtistDetail {
  artist: MusicArtist;
  albums: MusicAlbum[];
}

export interface LibrarySummary {
  libraryId: string;
  stats: {
    movies: number;
    shows: number;
    total: number;
    durationMs: number;
    newestAt: number | null;
  };
  scannedAt: number | null;
  layer: 'hot' | 'cold';
  reachable: boolean;
  unavailableReason: string | null;
  recent: Item[];
}

/** Kütüphane sıralaması (Tasarım V4, Öneri 8). */
export type LibrarySort = 'title' | 'added' | 'year' | 'duration';

/** İzlenme filtresi. */
export type LibraryFilter = 'all' | 'unwatched' | 'watched';

/**
 * Hesabı izleyen ana sayfa tercihleri (V5-15).
 *
 * Cihazda kalan oynatıcı tercihlerinden ayrıdır; sunucuda kullanıcıya bağlıdır.
 */
export interface HomePreferences {
  showContinue: boolean;
  showRecent: boolean;
  showCollections: boolean;
  showHistory: boolean;
  watchlistFirst: boolean;
}

export type HomeRailId = 'continue' | 'recent' | 'watchlist' | 'collections' | 'history';

/** Sunucuda tek istekte üretilen, tekrarsız kişisel ana sayfa rafları. */
export interface HomeRail {
  id: HomeRailId;
  title: string;
  items: Item[];
}

export interface HomeFeed {
  hero: Item | null;
  rails: HomeRail[];
}

/** Hesabı izleyen katalog tercihleri (V6-05). */
export interface CatalogPreferences {
  defaultLibrarySort: LibrarySort;
  watchlistLayout: 'grid' | 'list';
}

/** Disk durumu: `ok` · `warning` · `critical`. */
export type DiskState = 'ok' | 'warning' | 'critical';

export interface FilesystemUsage {
  /** Toplam (bayt). */
  totalBytes: number;
  /** Kullanıcıya kalan alan (bayt). */
  freeBytes: number;
  /** Kullanılan oran (0–1). */
  usedRatio: number;
}

export interface CacheUsage {
  path: string;
  usedBytes: number;
  /** Üst sınır (bayt); `null` = sınır bilinmiyor. */
  limitBytes: number | null;
  /** Sınıra göre oran (0–1). */
  ratio: number | null;
  state: DiskState;
}

export interface DiskStatus {
  /** pbfork veri dizininin bulunduğu dosya sistemi. */
  filesystem: FilesystemUsage | null;
  /** Transcode önbelleği. */
  transcode: CacheUsage;
  /** Uzak disk (rclone) önbelleği; yoksa `null`. */
  remoteCache: CacheUsage | null;
  /** Genel durum (en kötü alt durum). */
  state: DiskState;
  /** Kullanıcıya gösterilecek uyarı metinleri (sunucunun diliyle). */
  messages: string[];
}

export interface ProfileSummary {
  id: string;
  name: string;
}
export interface Capabilities {
  serverVersion: string;
  profiles: ProfileSummary[];
  transcode: { hevcHardwareEncode: boolean; available: boolean; note: string };
  progress: {
    reportIntervalMs: number;
    states: string[];
    watchedThresholdRatio: number;
  };
  decision: { endpoint: string; expiresInSeconds: number };
}

export interface DecisionReason {
  code: string;
  detail: string;
}

export interface PlaybackDecision {
  sessionId: string;
  mode: 'direct_play' | 'direct_stream' | 'transcode' | 'unplayable';
  targetProfile: string | null;
  protocol: string;
  startPositionMs: number;
  /** İçeriğin katmanı (ADR-010). Soğuksa sunucu hedefi düşürmüş olur. */
  layer: 'hot' | 'cold';
  isAudio?: boolean;
  /**
   * Bearer istemci için öğe kapsamlı imzalı doğrudan akış URL'si.
   * Web/PWA'da `null`/yok; çerez oturumu kullanılır.
   */
  streamUrl?: string | null;
  /**
   * Bağlantının konumu — **sunucu türetir** (bağlantı adresinden).
   *
   * İstemcinin bildirdiği değer güvenilmez olduğu için yok sayılır; buradaki
   * değer yalnız bilgilendirme amaçlıdır.
   */
  location?: 'lan' | 'wan';
  /**
   * Etkin bant tavanı (bit/s).
   *
   * Karar ucu hesaplar (bağlantı bütçesi ∩ profil bütçesi ∩ istek). Dönüştürme
   * gerekiyorsa **çıktı da bu tavanın altında** olmalıdır → istemci bu değeri
   * dönüştürme isteğine taşır.
   */
  maxBitrate?: number | null;
  reasons: DecisionReason[];
  specRefs: string[];
  transcode: { profile: string; startUrl: string; tokenTtlSeconds: number } | null;
}

export interface TranscodeJob {
  jobId: string;
  state: 'queued' | 'running' | 'ready' | 'failed' | 'cancelled';
  profile: string;
  /**
   * Segmentlerin zaman çizelgesinde başladığı kaynak konumu (ms).
   *
   * Kalite düşürmesinde film baştan kodlanmaz; iş mevcut konumdan başlar.
   * Bu alan hls.js'in `timelineOffset` ayarına gider. Eski sunucular
   * göndermez → 0 kabul edilir (davranış değişmez).
   */
  startPositionMs?: number;
  /**
   * Üretilen akışın **gerçekte** başladığı kaynak konumu (ms).
   *
   * İlk segmentler üretildikten sonra **ölçülür** (durum ucundan gelir):
   * FFmpeg giriş araması kaynağın en yakın IDR'sine atlar ve HEVC'de bu
   * istenenden saniyeler önce olabilir. Zaman çizelgesi buna göre kurulur.
   */
  actualStartMs?: number | null;
  statusUrl: string;
  playlistUrl: string;
  segmentUrlTemplate: string;
  segmentSeconds: number;
}

export interface JobStatus {
  jobId: string;
  state: TranscodeJob['state'];
  /**
   * Oynatılabilir mi? `state` `ready` olmadan da oynatma başlayabilir:
   * sunucu FFmpeg'i bitirmeyi beklemeden ilk segmentleri yayınlamaya başlar.
   * Eski sunucular alanı göndermez; o durumda davranış `state === 'ready'`
   * denkliktir (geriye uyumlu).
   */
  playable?: boolean;
  profile: string;
  progressRatio: number | null;
  error: string | null;
  /**
   * Üretilen akışın **gerçekte** başladığı kaynak konumu (ms).
   *
   * Sunucu "600. saniyeden başladım" der; FFmpeg giriş araması kaynağın en
   * yakın IDR'sine atladığı için HEVC'de içerik istenenden ~9 sn erken
   * başlayabilir (ölçüm: istenen 600.0, gerçek 591.4). İstemci zaman
   * çizelgesini **buna** göre kurar; yoksa oynatma takılır.
   *
   * Eski sunucular alanı göndermez → `null`; o durumda istenen konum kullanılır.
   */
  actualStartMs?: number | null;
}

export interface NativePlaybackTicket {
  itemId: string;
  /** Aynı origin'de imzalı, çerez gerektirmeyen stream yolu. */
  url: string;
  /** Native servisin periyodik ilerleme raporu için aynı imzalı bilet. */
  progressToken: string;
  expiresAt: number;
  title: string;
  artist: string | null;
  album: string | null;
  durationMs: number | null;
  profileId?: string | null;
}

export interface NativePlaybackTickets {
  tickets: Array<Omit<NativePlaybackTicket, 'profileId'>>;
  profileId: string | null;
}

export interface PlaybackSession {
  sessionId: string;
  itemId: string;
  resumeFromMs: number;
  reportIntervalMs: number;
}

/** Sunucu hata gövdesi; her hata `error` alanı taşır. */
export class ApiError extends Error {
  constructor(
    readonly status: number,
    readonly code: string,
    message: string,
  ) {
    super(message);
    this.name = 'ApiError';
  }

  /** 401 → oturum düştü, istemci giriş ekranına dönmeli. */
  get isUnauthorized(): boolean {
    return this.status === 401;
  }
}

async function request<T>(path: string, init?: RequestInit): Promise<T> {
  const token = await getAuthToken();
  const response = await fetch(apiUrl(path), {
    // Web/PWA'da oturum çerezi aynı kökten gider. Bearer modunda çerez gerekmez.
    credentials: token === null ? 'same-origin' : 'omit',
    ...init,
    headers: {
      Accept: 'application/json',
      ...(init?.body ? { 'Content-Type': 'application/json' } : {}),
      ...(token === null ? {} : { Authorization: `Bearer ${token}` }),
      ...init?.headers,
    },
  });

  if (response.status === 204) return undefined as T;

  const text = await response.text();
  const body: unknown = text.length > 0 ? safeParse(text) : undefined;

  if (!response.ok) {
    const payload = (body ?? {}) as { error?: string; message?: string };
    throw new ApiError(
      response.status,
      payload.error ?? 'unknown',
      payload.message ?? `İstek başarısız (${response.status})`,
    );
  }
  return absolutizeApiUrls(body as T, API_BASE_URL);
}

function safeParse(text: string): unknown {
  try {
    return JSON.parse(text);
  } catch (error) {
    recordSwallowed('api.parse', error);
    return undefined;
  }
}

const post = <T>(path: string, body?: unknown): Promise<T> =>
  request<T>(path, {
    method: 'POST',
    ...(body === undefined ? {} : { body: JSON.stringify(body) }),
  });

export const api = {
  // --- Kimlik ---------------------------------------------------------------
  async me(): Promise<User | null> {
    try {
      const { user } = await request<{ user: User }>('/api/auth/me');
      return user;
    } catch (error) {
      if (error instanceof ApiError && error.isUnauthorized) return null;
      throw error;
    }
  },

  async login(username: string, password: string, twoFactorCode?: string) {
    if (usesBearerAuth) {
      const result = await post<{
        user?: User;
        token?: string;
        expiresAt?: number;
        require2fa?: boolean;
        message?: string;
      }>('/api/auth/token', {
        username,
        password,
        deviceName: 'android',
        twoFactorCode,
      });
      if (result.token !== undefined) {
        await setAuthToken(result.token, result.expiresAt);
      }
      return result;
    }
    return post<{ user?: User; require2fa?: boolean; message?: string }>('/api/auth/login', {
      username,
      password,
      deviceName: 'web',
      twoFactorCode,
    });
  },

  setup2fa: () => post<{ secret: string; uri: string }>('/api/auth/2fa/setup'),

  enable2fa: (code: string) =>
    post<{ ok: true; message: string }>('/api/auth/2fa/enable', { code }),

  disable2fa: (password: string) =>
    post<{ ok: true; message: string }>('/api/auth/2fa/disable', { password }),

  async logout(): Promise<void> {
    if (usesBearerAuth) {
      try {
        await request<void>('/api/auth/token/logout', { method: 'POST' });
      } finally {
        await clearAuthToken();
      }
      return;
    }
    await request<void>('/api/auth/logout', { method: 'POST' });
  },

  /**
   * Oturumdaki kullanıcının parolasını değiştirir.
   *
   * Sunucu mevcut parolayı doğrular ve **diğer cihazların oturumlarını
   * düşürür**; bu oturum açık kalır. Gerçek sunucuda parola değiştirmenin
   * hiçbir yolu yoktu (`ensureInitialAdmin` yalnız "hiç kullanıcı yokken"
   * çalışır), bu yüzden yol açık.
   */
  changePassword: (currentPassword: string, newPassword: string) =>
    post<{ ok: true; revokedSessions: number }>('/api/auth/password', {
      currentPassword,
      newPassword,
    }),

  // --- Yetenek --------------------------------------------------------------
  capabilities: (signal?: AbortSignal) =>
    request<Capabilities>('/api/capabilities', signal === undefined ? undefined : { signal }),

  /** Soğuk katman erişilebilirliği özeti (ADR-011). */
  layerHealth: () =>
    request<{
      cold: number;
      unreachable: Array<{ id: string; name: string; reason: string }>;
      checked: boolean;
    }>('/api/capabilities/layers'),

  /**
   * Disk kullanımı ve kotaları (ADR-014).
   *
   * "Sunucumda ne kadar boş alan var?" sorusunun tek cevabı. Eşikleri
   * **sunucu** değerlendirir; istemci yalnız sonucu gösterir.
   */
  disk: () => request<DiskStatus>('/api/disk'),

  // --- Katalog --------------------------------------------------------------
  libraries: () => request<{ libraries: Library[] }>('/api/libraries').then((r) => r.libraries),

  createLibrary: (input: {
    name: string;
    kind: 'movie' | 'show' | 'mixed' | 'music';
    path: string;
    layer?: 'hot' | 'cold';
    /**
     * Yan dosya altyazı klasörü (ADR-016).
     *
     * Boş bırakılırsa yalnız medyanın kendi klasörü aranır.
     */
    subtitlePath?: string;
  }) => post<Library>('/api/libraries', input),

  deleteLibrary: (libraryId: string) =>
    request<{ success: boolean; id: string }>(`/api/libraries/${encodeURIComponent(libraryId)}`, {
      method: 'DELETE',
    }),

  scanLibrary: (libraryId: string, probe = true, force = false) =>
    post<{ libraryId: string; started: boolean }>(`/api/libraries/${libraryId}/scan`, {
      probe,
      ...(force ? { force: true } : {}),
    }),

  scanStatus: (libraryId: string) =>
    request<{
      running: boolean;
      done: number;
      total: number;
      current: string;
      elapsedMs?: number;
      result?: Record<string, unknown> | null;
      error?: string | null;
    }>(`/api/libraries/${libraryId}/scan`),

  libraryItems: (
    libraryId: string,
    offset = 0,
    limit = 50,
    sort: LibrarySort = 'title',
    filter: LibraryFilter = 'all',
  ) =>
    request<{
      items: Item[];
      total: number;
      sort: LibrarySort;
      filter: LibraryFilter;
    }>(
      `/api/libraries/${libraryId}/items?offset=${offset}&limit=${limit}&sort=${sort}&filter=${filter}`,
    ),

  /**
   * Kütüphane özeti — istatistikler + son eklenenler (dalga 2, karar 3).
   *
   * Az içerikli kütüphanelerde ekran "kesilmiş" gibi görünüyordu; boşluğu
   * dolduran iki dürüst veri kaynağı burada toplanıyor. Ana listeden ayrı
   * tutulmasının nedeni: sayfalama mantığı ikinci bir yere kopyalanmasın.
   */
  librarySummary: (libraryId: string, recent = 12) =>
    request<LibrarySummary>(`/api/libraries/${libraryId}/summary?recent=${recent}`),

  item: (itemId: string) => request<ItemDetail>(`/api/items/${itemId}`),

  itemCollection: (itemId: string) => request<ItemCollection>(`/api/items/${itemId}/collection`),

  setWatched: (itemId: string, value: boolean) =>
    value
      ? post<{ isWatched: boolean }>(`/api/items/${itemId}/watched`)
      : request<{ isWatched: boolean }>(`/api/items/${itemId}/watched`, {
          method: 'DELETE',
        }),

  clearProgress: (itemId: string) =>
    request<{ ok: true; isWatched: boolean }>(`/api/items/${itemId}/progress`, {
      method: 'DELETE',
    }),

  /** Kalıcı silme: medya dosyaları diskten/uzak bağlantıdan silinir. */
  deleteItem: (itemId: string) =>
    request<{
      ok: boolean;
      deletedFiles: number;
      missingFiles: number;
      deletedItems: number;
    }>(`/api/items/${encodeURIComponent(itemId)}`, { method: 'DELETE' }),

  // --- Harici metadata (Tasarım V4, Öneri 13) ------------------------------
  itemMetadata: (itemId: string) =>
    request<{ enabled: boolean; provider: string | null; metadata: ItemMetadata | null }>(
      `/api/items/${itemId}/metadata`,
    ),

  refreshMetadata: (itemId: string) =>
    post<{ status: string; metadata: ItemMetadata | null }>(
      `/api/items/${itemId}/metadata/refresh`,
    ),

  searchMetadata: (query: string, kind: 'movie' | 'show') =>
    request<{ candidates: MetadataCandidate[] }>(
      `/api/metadata/search?q=${encodeURIComponent(query)}&kind=${kind}`,
    ),

  matchMetadata: (itemId: string, externalId: string) =>
    post<{ metadata: ItemMetadata }>(`/api/items/${itemId}/metadata/match`, { externalId }),

  clearMetadata: (itemId: string) =>
    request<{ ok: boolean }>(`/api/items/${itemId}/metadata`, { method: 'DELETE' }),

  /** Manuel künye düzenlemesi; yalnız gönderilen alanlar değişir. */
  updateMetadata: (itemId: string, patch: MetadataOverrideInput) =>
    request<{ metadata: ItemMetadata | null }>(`/api/items/${itemId}/metadata`, {
      method: 'PATCH',
      body: JSON.stringify(patch),
    }),

  /** Afiş/backdrop yükle (base64). */
  uploadArtwork: (itemId: string, input: { kind: 'poster' | 'backdrop'; data: string }) =>
    post<{
      ok: boolean;
      kind: 'poster' | 'backdrop';
      source: 'upload';
      artwork: {
        posterSource: 'upload' | 'sidecar' | null;
        backdropSource: 'upload' | 'sidecar' | null;
      };
    }>(`/api/items/${itemId}/artwork`, input),

  /** Yüklenen afişi kaldırır; klasördeki yan dosya varsa o görünür. */
  deleteArtwork: (itemId: string, kind: 'poster' | 'backdrop') =>
    request<{
      ok: boolean;
      artwork: {
        posterSource: 'upload' | 'sidecar' | null;
        backdropSource: 'upload' | 'sidecar' | null;
      };
    }>(`/api/items/${itemId}/artwork/${kind}`, { method: 'DELETE' }),

  /** Yönetici metadata gelen kutusu (V5-12). */
  adminMetadataInbox: (limit = 100) =>
    request<{ enabled: boolean; threshold: number; items: AdminMetadataItem[] }>(
      `/api/admin/metadata/inbox?limit=${limit}`,
    ),

  adminApproveMetadata: (itemIds: string[]) =>
    post<{ approved: string[]; skipped: string[] }>('/api/admin/metadata/approve', { itemIds }),

  adminRefreshMetadata: (itemIds: string[]) =>
    post<{ results: Array<{ itemId: string; status: string; error?: string }> }>(
      '/api/admin/metadata/refresh',
      { itemIds },
    ),

  /** Yönetici kullanıcı yönetimi (V6-04). */
  adminUsers: () => request<{ users: AdminUser[] }>('/api/admin/users'),

  adminCreateUser: (input: {
    username: string;
    displayName: string;
    role: 'user' | 'admin';
    password: string;
  }) => post<{ user: AdminUser }>('/api/admin/users', input),

  adminUpdateUser: (
    userId: string,
    patch: { displayName?: string; role?: 'user' | 'admin'; disabled?: boolean },
  ) =>
    request<{ user: AdminUser }>(`/api/admin/users/${encodeURIComponent(userId)}`, {
      method: 'PATCH',
      body: JSON.stringify(patch),
    }),

  adminResetUserPassword: (userId: string, password: string) =>
    post<{ ok: true }>(`/api/admin/users/${encodeURIComponent(userId)}/reset-password`, {
      password,
    }),

  adminRevokeUserSessions: (userId: string) =>
    post<{ ok: true }>(`/api/admin/users/${encodeURIComponent(userId)}/revoke-sessions`),

  /** Yönetici sistem özeti (V6-11). */
  adminSystem: () => request<AdminSystem>('/api/admin/system'),

  /** Yönetici oturum merkezi (V5-12). */
  adminSessions: () => request<{ sessions: AdminSession[] }>('/api/admin/sessions'),

  adminRevokeSession: (sessionId: string) =>
    request<{ revoked: boolean }>(`/api/admin/sessions/${encodeURIComponent(sessionId)}`, {
      method: 'DELETE',
    }),

  adminRevokeOtherSessions: () => post<{ revoked: number }>('/api/admin/sessions/revoke-others'),

  /** Kullanıcı ana sayfa tercihleri (V5-15). */
  homePreferences: () => request<HomePreferences>('/api/me/preferences/home'),

  updateHomePreferences: (value: HomePreferences) =>
    request<HomePreferences>('/api/me/preferences/home', {
      method: 'PUT',
      body: JSON.stringify(value),
    }),

  resetHomePreferences: () =>
    request<HomePreferences>('/api/me/preferences/home', { method: 'DELETE' }),

  /** Tek istekte kişisel ana sayfa rafları (V6-06). */
  home: () => request<HomeFeed>('/api/me/home'),

  /** Albüm ve sanatçı arşivi (V6-07). */
  music: () => request<MusicArchive>('/api/music'),
  musicAlbum: (albumId: string) =>
    request<MusicAlbumDetail>(`/api/music/albums/${encodeURIComponent(albumId)}`),
  musicArtist: (artistId: string) =>
    request<MusicArtistDetail>(`/api/music/artists/${encodeURIComponent(artistId)}`),

  /** Albüm künyesini düzenle (yönetici). */
  musicUpdateAlbum: (albumId: string, patch: MusicAlbumPatch) =>
    request<{ album: MusicAlbum }>(`/api/music/albums/${encodeURIComponent(albumId)}`, {
      method: 'PATCH',
      body: JSON.stringify(patch),
    }),

  /** Albüm künyesi düzenlemesini sıfırla (parça etiketlerine dön). */
  musicResetAlbum: (albumId: string) =>
    request<{ album: MusicAlbum }>(`/api/music/albums/${encodeURIComponent(albumId)}/metadata`, {
      method: 'DELETE',
    }),

  /** Albüm kapağı yükle (base64). */
  musicUploadCover: (albumId: string, data: string) =>
    post<{ album: MusicAlbum }>(`/api/music/albums/${encodeURIComponent(albumId)}/cover`, {
      data,
    }),

  /** Yüklenen albüm kapağını kaldır; klasör kapağı varsa o görünür. */
  musicDeleteCover: (albumId: string) =>
    request<{ album: MusicAlbum }>(`/api/music/albums/${encodeURIComponent(albumId)}/cover`, {
      method: 'DELETE',
    }),

  /** Kullanıcı katalog tercihleri (V6-05). */
  catalogPreferences: () => request<CatalogPreferences>('/api/me/preferences/catalog'),

  updateCatalogPreferences: (value: CatalogPreferences) =>
    request<CatalogPreferences>('/api/me/preferences/catalog', {
      method: 'PUT',
      body: JSON.stringify(value),
    }),

  resetCatalogPreferences: () =>
    request<CatalogPreferences>('/api/me/preferences/catalog', { method: 'DELETE' }),

  /** Tüm kütüphanelerin ortak "Son Eklenenler" rafı. */
  recentlyAdded: () => request<{ items: Item[] }>('/api/me/recent').then((r) => r.items),

  /** İzleme listesi (Tasarım V4, Öneri 9). */
  watchlist: () => request<{ items: Item[] }>('/api/watchlist'),

  /** Listeye ekle/çıkar. */
  setWatchlist: (itemId: string, add: boolean) =>
    request<{ inWatchlist: boolean }>(`/api/items/${itemId}/watchlist`, {
      method: add ? 'POST' : 'DELETE',
    }),

  /**
   * Sıradaki içerik.
   *
   * Kural **sunucuda** çözülür (`apps/server/src/catalog/next.ts`): müzik
   * kütüphanesinde sıradaki **parça**, dizide sıradaki **bölüm**, filmde
   * sıradaki **yok**.
   *
   * Sıradaki yoksa `item` `null` döner — bu bir hata değil, bir durumdur;
   * istemci "sıradaki yok" düğmesini pasif gösterir.
   */
  nextItem: (itemId: string) =>
    request<{ item: Item | null; relation: 'track' | 'episode' | null }>(
      `/api/items/${encodeURIComponent(itemId)}/next`,
    ),

  search: (
    query: string,
    options: { kind?: SearchKind; yearFrom?: number | null; yearTo?: number | null } = {},
  ) => {
    const params = [`q=${encodeURIComponent(query)}`];
    if (options.kind !== undefined && options.kind !== 'all') {
      params.push(`kind=${options.kind}`);
    }
    if (options.yearFrom !== undefined && options.yearFrom !== null) {
      params.push(`yearFrom=${options.yearFrom}`);
    }
    if (options.yearTo !== undefined && options.yearTo !== null) {
      params.push(`yearTo=${options.yearTo}`);
    }
    return request<SearchResponse>(`/api/search?${params.join('&')}`);
  },

  continueWatching: () => request<{ items: Item[] }>('/api/me/continue').then((r) => r.items),

  // --- Oynatma --------------------------------------------------------------
  decide: (input: {
    itemId: string;
    profileId: string;
    startPositionMs?: number;
    location?: 'lan' | 'wan';
    allowDirectPlay?: boolean;
    allowDirectStream?: boolean;
    /**
     * Kullanıcının seçtiği kalite (ADR-011): `original` → kaynak olduğu gibi,
     * belirli bir yükseklik → sunucu o çözünürlükte dönüştürür.
     */
    quality?: 'auto' | 'original' | '1080p' | '720p' | '480p';
    /**
     * Kullanıcının seçtiği yöntem (ADR-011): `direct` → asla dönüştürme.
     */
    mode?: 'auto' | 'direct' | 'transcode';
    clientCapabilities?: {
      hevc?: boolean;
      hdr?: boolean;
      av1?: boolean;
      ac3?: boolean;
      eac3?: boolean;
    };
  }) => post<PlaybackDecision>('/api/playback/decide', { protocol: 'hls', ...input }),

  /**
   * Dönüştürme işi başlatır.
   *
   * `maxBitrate` (bit/s) verilirse **çıktı da bu tavana sığacak** biçimde
   * kodlanır (sabit bit hızı). Karar ucu tavanı hesaplar ve `decide` cevabında
   * bildirir.
   *
   * ## Tavan yoksa alan **gönderilmez**
   *
   * Karar ucu tavan olmadığında `maxBitrate: null` bildirir ("yerel oynatma,
   * CRF ile kodla"). Sunucunun şeması bu alanı `z.number().optional()` ile
   * tanımlar; `.optional()` **`null`ı kabul etmez** — `null` gönderilirse
   * istek 400 ile reddedilir.
   *
   * Gerçek hata (VPS'te yakalandı): masaüstü profilinin bütçe tavanı yoktur
   * (mobil profilin 4 Mbit/s bütçesi vardır), dolayısıyla yerel oynatmada
   * masaüstü `null` gönderiyor, mobil ise sayı gönderiyordu. Sonuç: filmler
   * masaüstünde 400 ile açılmıyor, müzikler (doğrudan oynatma) açılıyordu.
   *
   * Bu yüzden tavan yoksa alan **tamamen atlanır** — `undefined` gövdeye
   * yazılmaz, sunucu "tavan yok" varsayılanını kullanır.
   */
  startTranscode: (
    itemId: string,
    profile: string,
    maxBitrate?: number | null,
    audioStreamIndex?: number,
    /**
     * Kalite düşürmesinde **mevcut oynatma konumu** (ms). Sunucu filmi
     * baştan değil buradan kodlar; HLS zaman çizelgesi de bu konumdan başlar.
     */
    startPositionMs?: number,
  ) =>
    post<TranscodeJob>('/api/playback/transcode', {
      itemId,
      profile,
      ...(typeof maxBitrate === 'number' ? { maxBitrate } : {}),
      ...(audioStreamIndex === undefined ? {} : { audioStreamIndex }),
      ...(startPositionMs !== undefined && startPositionMs > 0 ? { startPositionMs } : {}),
    }),

  jobStatus: (jobId: string) => request<JobStatus>(`/api/transcode/${jobId}/status`),

  cancelJob: (jobId: string) => request<void>(`/api/transcode/${jobId}`, { method: 'DELETE' }),

  /** Sayfa kapanırken kodlamayı iptal eder; `keepalive` gerektiği için ayrı yol. */
  async cancelJobKeepalive(jobId: string): Promise<void> {
    const token = await getAuthToken();
    await fetch(apiUrl(`/api/transcode/${jobId}`), {
      method: 'DELETE',
      keepalive: true,
      credentials: token === null ? 'include' : 'omit',
      headers: token === null ? {} : { Authorization: `Bearer ${token}` },
    });
  },

  /** Doğrudan oynatma akışı: tarayıcı Range başlığını kendisi yönetir. */
  streamUrl: (itemId: string) => mediaUrl(`/api/items/${itemId}/stream`),

  /** Altyazı akışı (WebVTT). Tarayıcı `<track src>` ile kullanır. */
  subtitleUrl: (itemId: string, key: string) => mediaUrl(`/api/items/${itemId}/subtitle/${key}`),

  /** Altyazı hazırlık durumları (Faz 3). */
  subtitleStatus: (itemId: string) =>
    request<SubtitleStatus>(`/api/items/${encodeURIComponent(itemId)}/subtitles/status`),

  /** Öğenin metin altyazılarını arka planda hazırlamayı ister. */
  prepareSubtitles: (itemId: string) =>
    post<{ state: string }>(`/api/items/${encodeURIComponent(itemId)}/subtitles/prepare`),

  /**
   * Altyazıyı **metin olarak** çeker (MSE yolu için).
   *
   * `<track>` yalnız doğrudan oynatmada çalışır; hls.js/MSE onu sessizce
   * yoksayar. Bu yüzden altyazı bindirmesi (overlay) WebVTT metnini kendisi
   * ayrıştırır. Bkz. `useSubtitleOverlay`.
   */
  subtitleText: async (itemId: string, key: string) => {
    const token = await getAuthToken();
    const response = await fetch(apiUrl(`/api/items/${itemId}/subtitle/${key}`), {
      credentials: token === null ? 'same-origin' : 'omit',
      headers: {
        Accept: 'text/vtt, text/plain',
        ...(token === null ? {} : { Authorization: `Bearer ${token}` }),
      },
    });
    if (!response.ok) {
      if (response.status === 415) {
        throw new ApiError(
          415,
          'burn_required',
          'Bu altyazı Blu-ray resim (PGS/Bitmap) biçimindedir. Tarayıcıda doğrudan metin olarak açılamaz; video üzerine yakılmalıdır.',
        );
      }
      throw new ApiError(response.status, 'subtitle_unavailable', 'Altyazı okunamadı.');
    }
    // `request<T>` JSON ayrıştırıyordu; WebVTT düz metindir.
    return response.text();
  },

  // --- İzleme raporu --------------------------------------------------------
  startSession: (itemId: string, profileId: string, positionMs: number) =>
    post<PlaybackSession>('/api/playback/sessions', { itemId, profileId, positionMs }),

  /** Native Media3 servisi için çerez taşımayan imzalı akış bileti. */
  nativeTicket: (itemId: string, profileId: string) =>
    post<NativePlaybackTicket>('/api/playback/native-ticket', { itemId, profileId }),

  /** Native Media3 kuyruğu için toplu imzalı akış biletleri. */
  nativeTickets: (itemIds: string[], profileId: string) =>
    post<NativePlaybackTickets>('/api/playback/native-tickets', { itemIds, profileId }),

  reportProgress: (
    sessionId: string,
    report: { sessionPositionMs: number; sourcePositionMs: number; state: string },
  ) =>
    post<{ isWatched: boolean; positionMs: number }>(
      `/api/playback/sessions/${sessionId}/progress`,
      report,
    ),

  // --- Canlı TV (IPTV) ------------------------------------------------------

  /** Eklenen çalma listesi kaynağı. */
  iptvSources: () => request<{ sources: IptvSource[] }>('/api/iptv/sources').then((r) => r.sources),

  /**
   * Yeni kaynak ekler ve ilk senkronizasyonu yapar.
   *
   * `kind: 'xtream'` ise `username`/`password` gerekir; parola sunucuda
   * şifrelenerek saklanır.
   */
  addIptvSource: (input: {
    name: string;
    url: string;
    kind?: 'm3u' | 'xtream';
    username?: string;
    password?: string;
    epgUrl?: string;
  }) =>
    post<{ source: { id: string; name: string; channelCount: number } }>(
      '/api/iptv/sources',
      input,
    ),

  /** Kaynağı siler (kanalları da silinir). */
  deleteIptvSource: (id: string) =>
    request<{ ok: boolean }>(`/api/iptv/sources/${encodeURIComponent(id)}`, {
      method: 'DELETE',
    }),

  /** Kaynağı yeniden çeker. */
  refreshIptvSource: (id: string) =>
    post<{ channelCount: number; skipped: number }>(
      `/api/iptv/sources/${encodeURIComponent(id)}/refresh`,
    ),

  /**
   * Yasal aday listeleri **sunucudan** ölçülür.
   *
   * "Hangi liste sağlam?" sorusunun cevabı sunucunun bulunduğu ülkeye bağlıdır;
   * bu yüzden ölçüm kullanıcının ağından değil, pbfork sunucusundan yapılır.
   */
  iptvRecommendedSources: (force?: boolean) =>
    request<{ sources: IptvRecommendedSource[] }>(
      `/api/iptv/recommended-sources${force === true ? '?force=1' : ''}`,
    ).then((r) => r.sources),

  /** Kullanıcının kendi liste adresini ölçer (kaynak eklenmeden önce). */
  probeIptvUrl: (url: string) =>
    post<{ probe: IptvProbeResult }>('/api/iptv/sources/probe', { url }).then((r) => r.probe),

  /** Kanal listesi. Akış adresi **gelmez** (sunucu imzalı adresi saklar). */
  iptvChannels: (params?: {
    source?: string | undefined;
    group?: string | undefined;
    q?: string | undefined;
  }) => {
    const query = new URLSearchParams();
    if (params?.source) query.set('source', params.source);
    if (params?.group) query.set('group', params.group);
    if (params?.q) query.set('q', params.q);
    const suffix = query.toString();
    return request<{ channels: IptvChannel[] }>(
      `/api/iptv/channels${suffix.length > 0 ? `?${suffix}` : ''}`,
    ).then((r) => r.channels);
  },

  /**
   * Kanal için oynatma jetonu.
   *
   * Jeton bir saat geçerlidir ve **yalnız bu kanalı** açar; akış adresi
   * istemciye hiç taşınmaz.
   */
  iptvPlayback: (channelId: string) =>
    post<{ url: string; expiresAt: number }>(
      `/api/iptv/channels/${encodeURIComponent(channelId)}/playback`,
    ),

  /**
   * Rehber penceresi.
   *
   * Zamanlar **UTC epoch ms**; sunucu yerel zamana çevirmekten vazgeçti (istemci
   * kendi saat diliminde gösterir).
   */
  iptvEpg: (params: { source: string; from?: number; to?: number }) => {
    const query = new URLSearchParams({ source: params.source });
    if (params.from !== undefined) query.set('from', String(params.from));
    if (params.to !== undefined) query.set('to', String(params.to));
    return request<{ channels: IptvGuideChannel[] }>(`/api/iptv/epg?${query.toString()}`).then(
      (r) => r.channels,
    );
  },

  /** Rehberi yeniden çeker (XMLTV dosyası büyük olabilir). */
  iptvRefreshEpg: (sourceId: string) =>
    post<{ channels: number; programs: number; matchedChannels: number }>(
      `/api/iptv/sources/${encodeURIComponent(sourceId)}/epg/refresh`,
    ),

  /** Favori kanal kimlikleri. */
  iptvFavorites: () =>
    request<{ favorites: string[] }>('/api/iptv/favorites').then((r) => r.favorites),

  /** Kanalı favorilere ekler. */
  addIptvFavorite: (channelId: string) =>
    request<{ ok: boolean; favorites: string[] }>(
      `/api/iptv/favorites/${encodeURIComponent(channelId)}`,
      { method: 'PUT' },
    ).then((r) => r.favorites),

  /** Favoriyi kaldırır. */
  removeIptvFavorite: (channelId: string) =>
    request<{ ok: boolean; favorites: string[] }>(
      `/api/iptv/favorites/${encodeURIComponent(channelId)}`,
      { method: 'DELETE' },
    ).then((r) => r.favorites),

  // --- Canlı TV: çocuk kilidi, catch-up, kayıtlar ---------------------------

  /** Kilitli kanallar ve PIN belirlenmiş mi? */
  iptvLocks: () => request<{ locks: string[]; hasPin: boolean }>('/api/iptv/locks'),

  /** PIN belirler (sunucuda Argon2id özeti olarak saklanır). */
  setIptvPin: (pin: string) =>
    request<{ ok: boolean }>('/api/iptv/settings/pin', {
      method: 'PUT',
      body: JSON.stringify({ pin }),
    }),

  /** PIN'i (ve kilitleri) kaldırır. */
  removeIptvPin: () => request<{ ok: boolean }>('/api/iptv/settings/pin', { method: 'DELETE' }),

  /** Kanalı kilitler. */
  lockIptvChannel: (channelId: string) =>
    request<{ ok: boolean; locks: string[] }>(`/api/iptv/locks/${encodeURIComponent(channelId)}`, {
      method: 'PUT',
    }).then((r) => r.locks),

  /** Kilidi kaldırır. */
  unlockIptvChannel: (channelId: string) =>
    request<{ ok: boolean; locks: string[] }>(`/api/iptv/locks/${encodeURIComponent(channelId)}`, {
      method: 'DELETE',
    }).then((r) => r.locks),

  /** Kilitli kanalın PIN'ini doğrular. */
  verifyIptvPin: (channelId: string, pin: string) =>
    request<{ ok: boolean }>(`/api/iptv/locks/${encodeURIComponent(channelId)}/unlock`, {
      method: 'POST',
      body: JSON.stringify({ pin }),
    }),

  /**
   * Geçmiş yayın (catch-up) adresi.
   *
   * Arşiv, canlı akışla **aynı oynatıcıda** açılır: sunucu arşiv playlist'ini
   * relay üzerinden verir.
   */
  iptvCatchupUrl: (channelId: string, token: string, startMs: number, endMs: number): string =>
    `/api/iptv/catchup/${encodeURIComponent(channelId)}?token=${encodeURIComponent(
      token,
    )}&start=${startMs}&end=${endMs}`,

  /** Kayıtları listeler. */
  iptvRecordings: () =>
    request<{
      recordings: Array<{
        id: string;
        channelId: string;
        title: string;
        startedAt: number;
        endedAt: number | null;
        sizeBytes: number;
        state: string;
      }>;
    }>('/api/iptv/recordings').then((r) => r.recordings),

  /** Kaydı başlatır. */
  startIptvRecording: (channelId: string, title?: string) =>
    request<{ id: string; startedAt: number }>('/api/iptv/recordings', {
      method: 'POST',
      body: JSON.stringify({ channelId, ...(title === undefined ? {} : { title }) }),
    }),

  /** Kaydı siler. */
  deleteIptvRecording: (id: string) =>
    request<{ ok: boolean }>(`/api/iptv/recordings/${encodeURIComponent(id)}`, {
      method: 'DELETE',
    }),
};
